import { Capacitor, registerPlugin } from '@capacitor/core';
import { invertRgba, restoreQrAreas, type QrArea } from './qrPolarity';

export type PrinterProtocol = 'tspl' | 'escpos';
export interface PairedPrinter { name: string; address: string }

interface NativePrinter {
  devices(): Promise<{ devices: PairedPrinter[] }>;
  connect(input: { address: string }): Promise<void>;
  write(input: { data: string }): Promise<void>;
  disconnect(): Promise<void>;
}

interface SerialWriter { write(data: Uint8Array): Promise<void>; releaseLock(): void }
interface SerialPortLike { open(input: { baudRate: number }): Promise<void>; close(): Promise<void>; writable: { getWriter(): SerialWriter } | null }
interface SerialNavigator { requestPort(): Promise<SerialPortLike>; getPorts(): Promise<SerialPortLike[]> }

const native = registerPlugin<NativePrinter>('ClassicPrinter');
let port: SerialPortLike | null = null;
let connected = false;

export function current(): boolean { return connected; }

export function supported(): boolean {
  return Capacitor.isNativePlatform() || (window.isSecureContext && 'serial' in navigator);
}

export async function paired(): Promise<PairedPrinter[]> {
  return Capacitor.isNativePlatform() ? (await native.devices()).devices : [];
}

export async function connect(address?: string): Promise<void> {
  if (Capacitor.isNativePlatform()) { if (!address) throw new Error('no_device'); await native.connect({ address }); connected = true; return; }
  const serial = (navigator as unknown as { serial: SerialNavigator }).serial;
  const granted = await serial.getPorts();
  port = granted.length === 1 && granted[0] ? granted[0] : await serial.requestPort();
  await port.open({ baudRate: 9600 });
  connected = true;
}

export async function disconnect(): Promise<void> {
  if (Capacitor.isNativePlatform()) await native.disconnect();
  else { await port?.close(); port = null; }
  connected = false;
}

function base64(data: Uint8Array): string {
  let raw = ''; for (let offset = 0; offset < data.length; offset += 0x8000) raw += String.fromCharCode(...data.subarray(offset, offset + 0x8000));
  return btoa(raw);
}

async function send(data: Uint8Array): Promise<void> {
  if (Capacitor.isNativePlatform()) { await native.write({ data: base64(data) }); return; }
  if (!port?.writable) throw new Error('not_connected');
  const writer = port.writable.getWriter(); try { await writer.write(data); } finally { writer.releaseLock(); }
}

async function raster(bitmap: Blob, qrAreas: readonly QrArea[] = [], mediaWidthMm?: number, inverted = false, scaleX = 1, scaleY = 1): Promise<{ data: Uint8Array; width: number; height: number; qrProtected: boolean }> {
  const image = await createImageBitmap(bitmap);
  const sourceWidth = Math.ceil(image.width / 8) * 8;
  const canvas = document.createElement('canvas'); canvas.width = sourceWidth; canvas.height = image.height;
  const context = canvas.getContext('2d', { willReadFrequently: true }); if (!context) throw new Error('raster_failed');
  context.fillStyle = '#fff'; context.fillRect(0, 0, sourceWidth, image.height); context.drawImage(image, 0, 0);
  let source = context.getImageData(0, 0, sourceWidth, image.height);
  const qrProtected = inverted && qrAreas.length > 0 && (Boolean(mediaWidthMm) || qrAreas.some((area) => area.unit === 'px'));
  if (qrProtected) {
    const normal = new Uint8ClampedArray(source.data);
    invertRgba(source.data);
    restoreQrAreas(source.data, normal, sourceWidth, image.height, qrAreas, mediaWidthMm ? image.width / mediaWidthMm : 1);
    context.putImageData(source, 0, 0);
  }
  const width = Math.ceil(sourceWidth * scaleX / 8) * 8;
  const height = Math.max(1, Math.round(image.height * scaleY));
  if (width !== sourceWidth || height !== image.height) {
    const scaled = document.createElement('canvas'); scaled.width = width; scaled.height = height;
    const scaledContext = scaled.getContext('2d', { willReadFrequently: true }); if (!scaledContext) throw new Error('raster_failed');
    scaledContext.fillStyle = '#fff'; scaledContext.fillRect(0, 0, width, height);
    scaledContext.imageSmoothingEnabled = false;
    scaledContext.drawImage(canvas, 0, 0, width, height);
    source = scaledContext.getImageData(0, 0, width, height);
  }
  const pixels = source.data;
  const data = new Uint8Array((width / 8) * height);
  for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
    const at = (y * width + x) * 4; const dark = (pixels[at] ?? 255) + (pixels[at + 1] ?? 255) + (pixels[at + 2] ?? 255) < 384;
    if (dark) { const byte = y * (width / 8) + Math.floor(x / 8); data[byte] = (data[byte] ?? 0) | (0x80 >> (x % 8)); }
  }
  return { data, width, height, qrProtected };
}

export function printerRaster(data: Uint8Array, protocol: PrinterProtocol, inverted: boolean): Uint8Array {
  // TSPL and ESC/POS assign opposite meanings to a set raster bit on the two
  // printers tested here. `inverted` describes the wanted paper, not the wire,
  // so callers do not have to remember that implementation detail.
  const flip = protocol === 'tspl' ? !inverted : inverted;
  return flip ? data.map((byte) => byte ^ 0xff) : data;
}

export interface PrintOptions {
  inverted?: boolean;
  density?: number | null;
  speed?: number | null;
  feedMm?: number;
  gapMm?: number;
  gapOffsetMm?: number;
  /** Width and height of one die-cut label, as opposed to the whole head. */
  mediaWidthMm?: number;
  mediaHeightMm?: number;
  /** Width of the physical print head. When wider than the label, centre it. */
  headWidthMm?: number;
  /** Profile-controlled media location; models only supply its initial value. */
  mediaAlignment?: 'left' | 'center';
  xOffsetMm?: number;
  yOffsetMm?: number;
  scaleX?: number;
  scaleY?: number;
  qrAreas?: QrArea[];
}

function millimetres(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/**
 * Set the label sensor geometry for each TSPL job.  Leaving this to the
 * printer's last app/driver configuration makes a 4-inch machine print a
 * narrow roll at the wrong origin or advance to the next gap.
 */
export function tsplSetup(options: PrintOptions): string {
  if (!options.mediaWidthMm || !options.mediaHeightMm) return '';
  const gap = Math.max(0, options.gapMm ?? 0);
  const gapOffset = Math.max(-3, Math.min(3, options.gapOffsetMm ?? 0));
  const headWidth = options.headWidthMm ?? options.mediaWidthMm;
  // Older callers predate profiles and centred any narrow label.  A managed
  // profile always supplies the field, so only its explicit `left` overrides
  // that compatible default.
  const centered = options.mediaAlignment !== 'left'
    ? Math.max(0, (headWidth - options.mediaWidthMm) / 2)
    : 0;
  // TSPL REFERENCE coordinates are head dots at 203 dpi (8 dots/mm).
  const x = Math.round((centered + (options.xOffsetMm ?? 0)) * 8);
  const y = Math.round((options.yOffsetMm ?? 0) * 8);
  return `SIZE ${millimetres(options.mediaWidthMm)} mm,${millimetres(options.mediaHeightMm)} mm\r\nGAP ${millimetres(gap)} mm,${millimetres(gapOffset)} mm\r\nREFERENCE ${x},${y}\r\n`;
}

function escFeed(mm: number): Uint8Array {
  let dots = Math.round(mm * 8); const bytes: number[] = [];
  while (dots > 0) { const step = Math.min(255, dots); bytes.push(0x1b, 0x4a, step); dots -= step; }
  return new Uint8Array(bytes);
}

export async function print(bitmap: Blob, protocol: PrinterProtocol, copies: number, options: PrintOptions = {}): Promise<void> {
  const image = await raster(bitmap, options.qrAreas, options.mediaWidthMm, options.inverted ?? false, options.scaleX ?? 1, options.scaleY ?? 1); const pixels = printerRaster(image.data, protocol, image.qrProtected ? false : options.inverted ?? false); const bytes = image.width / 8; const encoder = new TextEncoder();
  if (protocol === 'tspl') {
    const settings = `${tsplSetup(options)}${options.speed === null || options.speed === undefined ? '' : `SPEED ${options.speed}\r\n`}${options.density === null || options.density === undefined ? '' : `DENSITY ${options.density}\r\n`}`;
    const head = encoder.encode(`${settings}CLS\r\nBITMAP 0,0,${bytes},${image.height},0,`);
    const feed = Math.max(0, Math.round((options.feedMm ?? 0) * 8));
    const tail = encoder.encode(`\r\nPRINT ${copies},1\r\n${feed ? `FEED ${feed}\r\n` : ''}`);
    const job = new Uint8Array(head.length + pixels.length + tail.length); job.set(head); job.set(pixels, head.length); job.set(tail, head.length + pixels.length); await send(job);
  } else {
    for (let copy = 0; copy < copies; copy += 1) {
      const head = new Uint8Array([0x1b, 0x40, 0x1d, 0x76, 0x30, 0, bytes & 255, bytes >> 8, image.height & 255, image.height >> 8]);
      const feed = escFeed(options.feedMm ?? 0); const tail = new Uint8Array([0x0a, ...feed]); const job = new Uint8Array(head.length + pixels.length + tail.length); job.set(head); job.set(pixels, head.length); job.set(tail, head.length + pixels.length); await send(job);
    }
  }
}
